import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import {
  productProperty,
  System,
  isNotNullOrUndefined,
} from 'ngx-core-services';
import { Store } from '@ngrx/store';
import { map, switchMap, tap } from 'rxjs/operators';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import {
  CreateBlockReduxSupport,
  IReduxBlock,
} from '../../store/helpers/decorator';
import {
  selectedSystem,
  SystemsState,
  requestSystemDeviceDetails,
  selectedSystemUid,
} from '../../store';
import { selectSystemDeviceFirmwareDetailsBySelectedSystem } from '../../store/selectors/device-details.selectors';
import { getProductProperties } from './device.utils';

const getLabels = () => ({
  title: 'Device details',
  serialNumber: 'Serial number',
  deviceType: 'Device type',
  phase: 'Phase',
  inverter: 'Inverter',
  evCharge: 'Ev-charge',
  hybridInverter: 'Hybrid inverter',
  storageInverter: 'Storage inverter',
  photovoltaicInverter: 'Photovoltaic inverter',
  stickLogger: 'Stick logger',
  threePhase: 'Three phase',
  singlePhase: 'Single phase',
  ratedPower: 'Power',
  localeCode: 'en-EN',
  arm: 'ARM fw. version',
  dsps: 'DSPS fw. version',
  dspm: 'DSPM fw. version',
});

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-550': 550,
})
@Component({
  selector: 'lib-device-details',
  templateUrl: './device-details.component.html',
  styleUrls: ['./device-details.component.scss'],
})
export class DeviceDetailsComponent implements ICustomBlock, IReduxBlock {
  readonly _externalProperties$ = new BehaviorSubject<
    productProperty[] | undefined
  >(undefined);
  @Input() set externalDevices(properties: productProperty[] | undefined) {
    this._externalProperties$.next(properties);
  }
  @Input() uiFramework: 'material' | 'ionic' = 'material';

  @Input() productImageSrc?: string;

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  _labels = getLabels();
  readonly labels$ = new BehaviorSubject<typeof this._labels>(this._labels);
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.labels$.next(this._labels);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System | undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly requestDeviceFwInfo$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystem).pipe(isNotNullOrUndefined()),
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([systemId, currentSystem, deviceId, forceRefresh, instanceId]) => {
      const shouldReadFwFromSystem =
        currentSystem?.source === 'aws' &&
        (currentSystem.monitoringType === 'Solarman' ||
          currentSystem.monitoringType === 'AzzurroHub');

      if (shouldReadFwFromSystem) {
        return;
      }

      this.store.dispatch(
        requestSystemDeviceDetails({
          systemId,
          instanceId,
          deviceId: deviceId.toUpperCase(),
          forceRefresh,
        })
      );
    })
  );

  readonly device$ = combineLatest([
    this._instanceId$,
    this._deviceId$.asObservable(),
    this._externalProperties$.asObservable(),
  ]).pipe(
    switchMap(([instanceId, deviceId, externalProps]) =>
      this.store
        .select(
          selectSystemDeviceFirmwareDetailsBySelectedSystem(
            instanceId,
            deviceId
          )
        )
        .pipe(
          map((fwInfo) => {
            if (!deviceId) {
              return undefined;
            }

            const product = getProductProperties(
              deviceId,
              this.labels,
              this.uiFramework
            );
            if (externalProps) {
              product?.properties.push(...externalProps);
            }

            if (fwInfo?.ARM) {
              product?.properties.push({
                name: this.labels.arm,
                value: fwInfo.ARM.toUpperCase(),
                icon:
                  this.uiFramework === 'material'
                    ? 'memory'
                    : 'hardware-chip-outline',
              });
            }

            if (fwInfo?.DSPM) {
              product?.properties.push({
                name: this.labels.dspm,
                value: fwInfo.DSPM.toUpperCase(),
                icon:
                  this.uiFramework === 'material'
                    ? 'memory'
                    : 'hardware-chip-outline',
              });
            }

            if (fwInfo?.DSPS) {
              product?.properties.push({
                name: this.labels.dsps,
                value: fwInfo.DSPS.toUpperCase(),
                icon:
                  this.uiFramework === 'material'
                    ? 'memory'
                    : 'hardware-chip-outline',
              });
            }

            return product;
          })
        )
    )
  );

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2
  ) {}
}
